import Link from 'next/link';
import { CardProps } from './types';
import DirectusImage from '../image/DirectusImage';
import TitleH2 from '../common/TitleH2';
import ButtonLink from '../link/ButtonLink';
import Paragraph from '../common/Paragraph';
import Ball from '../icon/Ball';
import WysiwygBlock from '../blocks/WysiwygBlock';

export default function CardAction({
  callToActionUrl,
  displayDescription = true,
  displayButton = true,
  image,
  imageAlt,
  imagePosition = 'right',
  imageSizes,
  imageDimensions,
  eyebrow_text,
  title,
  description,
  imageBlur,
  wisiwyg,
  grayBackground,
}: CardProps) {
  let link = '';
  if (callToActionUrl?.url) {
    link = callToActionUrl.url;
  }

  const sizes = imageSizes || '(min-width: 66em) 20vw, (min-width: 44em) 20vw, 100vw';

  const background = grayBackground ? `bg-stone-50  rounded-md` : 'rounded-none';

  const extraEllipseBackground =
    imagePosition === 'left' ? 'lg:bg-card-action-left-ellipse' : 'lg:bg-card-action-ellipse';

  const extraEllipseBackgroundMobile =
    imagePosition === 'left'
      ? 'bg-card-action-left-figure-ellipse-mobile lg:bg-card-action-no-figure-left-ellipse-mobile'
      : 'bg-card-action-figure-ellipse-mobile lg:bg-card-action-no-figure-ellipse-mobile';

  const panel = (
    <div
      className={`card flex w-full flex-col ${background} ${extraEllipseBackground} py-10 lg:card-side lg:min-w-[1000px] lg:flex-row lg:p-20`}
    >
      {imagePosition === 'left' && image && (
        <figure
          className={`relative flex min-h-96 w-full lg:w-5/12 ${extraEllipseBackgroundMobile}`}
        >
          <div className="px-[15px] lg:px-0 w-full">
            <DirectusImage
              src={image || ''}
              alt={imageAlt || ''}
              blur={imageBlur}
              dimension={{ sizes, ...imageDimensions }}
              extraStyles={{ width: '100%', height: '100%' }}
              relative={true}
              styles="rounded-md"
            />
          </div>
        </figure>
      )}
      <div
        className={`flex w-full flex-col px-[15px] py-5 lg:w-7/12 lg:px-0 ${imagePosition === 'right' ? 'lg:pr-[100px]' : 'lg:pl-[100px]'} `}
      >
        {eyebrow_text && (
          <div className="inline-block pb-[20px] text-xs font-bold uppercase text-gray-400 lg:pb-[30px]">
            <span className="mr-2 inline-block align-sub">
              <Ball animated={false} />
            </span>
            {eyebrow_text}
          </div>
        )}
        <TitleH2
          customFont={true}
          color="black"
          heading={false}
          styles="mg-[15px] lg:mb-[25px]"
          titleTracking="tracking-wide"
          titleSize="text-[45px] lg:text-[65px] leading-[45px] lg:leading-[60px]"
        >
          {title}
        </TitleH2>
        {displayDescription && (
          <Paragraph styles="leading-10 text-xl lg:text-2xl">{description}</Paragraph>
        )}
        {wisiwyg && (
          <div className="grow">
            <div className="">
              <WysiwygBlock html={wisiwyg || ''} />
            </div>
          </div>
        )}
        {displayButton && (
          <div>
            {link !== '' && (
              <ButtonLink
                url={callToActionUrl}
                name={callToActionUrl.linkText || ''}
                styles="mt-[30px] text-slate-950"
              />
            )}
          </div>
        )}
      </div>
      {imagePosition === 'right' && image && (
        <figure
          className={`relative flex min-h-96 w-full lg:w-5/12 ${extraEllipseBackgroundMobile}`}
        >
          <div className="px-[15px] lg:px-0 w-full">
            <DirectusImage
              src={image || ''}
              alt={imageAlt || ''}
              blur={imageBlur}
              dimension={{ sizes, ...imageDimensions }}
              extraStyles={{ width: '100%', height: '100%' }}
              relative={true}
              styles="rounded-md"
            />
          </div>
        </figure>
      )}
    </div>
  );

  return displayButton ? panel : <Link href={link}>{panel}</Link>;
}
